import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Euro, TrendingUp, Users, Award, Download, AlertCircle, CheckCircle, ChevronDown, ChevronUp, UserPlus, Power, Trash2, AlertTriangle, Edit, Send, CreditCard, Copy, Check, Loader2, X, MessageSquare, Settings } from 'lucide-react';
import { motion } from 'framer-motion';
import ClientEditModal from '../../components/ClientEditModal';
import VendorOnboarding from '../../components/Vendor/VendorOnboarding';

const VendorDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutError, setPayoutError] = useState('');
  const [payoutSuccess, setPayoutSuccess] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Payment Link states
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState({
    email: '',
    full_name: '',
    amount: 59,
    plan_name: 'Abonnement IPTV 12 mois'
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);
  
  // IPTV Credentials Modal states
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [credentialsData, setCredentialsData] = useState({
    username: '',
    password: '',
    prefix: '',
    server_url: '',
    port: '',
    m3u_link: '',
    access_name: '',
    smarters_url: '',
    notes: ''
  });
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsSaved, setCredentialsSaved] = useState(false);
  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);
  const [existingCredentialId, setExistingCredentialId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [autoGenerated, setAutoGenerated] = useState(false);

  const vendorLink = `${window.location.origin}/inscription?vendor=${stats?.vendor_code || ''}`;

  useEffect(() => {
    fetchData();
  }, []);

  // Génération automatique des URLs quand prefix, username ou password changent
  useEffect(() => {
    if (credentialsData.username && credentialsData.password) {
      const prefix = credentialsData.prefix?.trim() || '';
      const username = credentialsData.username.trim();
      const password = credentialsData.password.trim();
      
      if (username && password) {
        // Si préfixe vide, domaine sans préfixe. Sinon avec préfixe.
        const serverDomain = prefix ? `${prefix}.xxx-tv4k-xxx.xyz` : 'xxx-tv4k-xxx.xyz';
        const smartersDomain = prefix ? `${prefix}.astrogol.com` : 'astrogol.com';
        
        setCredentialsData(prev => ({
          ...prev,
          server_url: `http://${serverDomain}`,
          m3u_link: `http://${serverDomain}/get.php?username=${username}&password=${password}&type=m3u_plus&output=mpegts`,
          smarters_url: `http://${smartersDomain}`
        }));
        setAutoGenerated(true);
      }
    } else {
      // Si username ou password vide, désactiver l'auto-génération
      setAutoGenerated(false);
    }
  }, [credentialsData.prefix, credentialsData.username, credentialsData.password]);

  const fetchData = async () => {
    try {
      const [statsRes, salesRes, clientsRes] = await Promise.all([
        api.get('/vendors/my-stats'),
        api.get('/vendors/my-sales'),
        api.get('/vendors/my-clients')
      ]);
      setStats(statsRes.data);
      setSales(salesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleClientStatus = async (clientId, e) => {
    e.stopPropagation();
    setActionLoading(clientId);
    try {
      await api.put(`/vendors/clients/${clientId}/toggle-status`);
      fetchData();
    } catch (error) {
      console.error('Error toggling client status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteClient = async (clientId) => {
    setActionLoading(clientId);
    try {
      await api.delete(`/vendors/clients/${clientId}`);
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting client:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getSubscriptionLabel = (client) => {
    // Compter le nombre d'abonnements actifs
    const activeCount = client.active_subscriptions_count || 0;
    
    if (activeCount === 0) {
      return { text: "0 abonnement", color: 'text-[#FF3B5C]', bg: 'bg-[#FF3B5C]/10' };
    } else if (activeCount === 1) {
      return { text: "1 abonnement actif", color: 'text-[#00FF88]', bg: 'bg-[#00FF88]/10' };
    } else {
      return { text: `${activeCount} abonnements actifs`, color: 'text-[#00FF88]', bg: 'bg-[#00FF88]/10' };
    }
  };

  const handlePayoutRequest = async (e) => {
    e.preventDefault();
    setPayoutError('');
    setPayoutSuccess(false);

    const amount = parseFloat(payoutAmount);
    if (amount < 50) {
      setPayoutError('Le montant minimum de retrait est de 50€');
      return;
    }

    if (amount > stats?.balance) {
      setPayoutError('Solde insuffisant');
      return;
    }

    try {
      await api.post('/vendors/request-payout', { amount });
      setPayoutSuccess(true);
      setPayoutAmount('');
      fetchData();
    } catch (error) {
      setPayoutError(error.response?.data?.detail || 'Erreur lors de la demande de paiement');
    }
  };

  const generatePaymentLink = async () => {
    if (!paymentLinkData.email || !paymentLinkData.full_name) return;
    
    setLinkLoading(true);
    try {
      const response = await api.post('/vendors/payment-links', paymentLinkData);
      setGeneratedLink(response.data.payment_url);
      setLinkSuccess(true);
      setLinkCopied(false);
    } catch (error) {
      console.error('Error generating payment link:', error);
      alert('Erreur lors de la génération du lien');
    } finally {
      setLinkLoading(false);
    }
  };

  const copyPaymentLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const resetPaymentLinkModal = () => {
    setShowPaymentLinkModal(false);
    setPaymentLinkData({ email: '', full_name: '', amount: 59, plan_name: 'Abonnement IPTV 12 mois' });
    setGeneratedLink('');
    setLinkSuccess(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { label: 'Actif', color: 'bg-[#00FF88]/10 text-[#00FF88]' },
      'pending_credentials': { label: 'En attente', color: 'bg-yellow-500/10 text-yellow-500' },
      'expired': { label: 'Expiré', color: 'bg-[#FF3B5C]/10 text-[#FF3B5C]' },
      'cancelled': { label: 'Annulé', color: 'bg-gray-500/10 text-gray-500' }
    };
    const badge = statusMap[status] || { label: status || 'Inconnu', color: 'bg-gray-500/10 text-gray-500' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const openCredentialsModal = async (sale) => {
    setSelectedSale(sale);
    setCredentialsSaved(false);
    setShowPassword(false);
    setCredentialsLoading(true);
    
    try {
      // Charger les identifiants existants s'ils existent
      const response = await api.get(`/vendors/sales/${sale.id}/credentials`);
      
      if (response.data.has_credentials) {
        // Mode modification
        setHasExistingCredentials(true);
        setExistingCredentialId(response.data.credentials.id);
        setCredentialsData({
          username: response.data.credentials.username || '',
          password: response.data.credentials.password || '',
          prefix: response.data.credentials.prefix || '',
          server_url: response.data.credentials.server_url || '',
          port: response.data.credentials.port || '',
          m3u_link: response.data.credentials.m3u_link || '',
          access_name: response.data.credentials.access_name || '',
          smarters_url: response.data.credentials.smarters_url || '',
          notes: response.data.credentials.notes || ''
        });
        // Si un préfixe existe, les URLs sont auto-générées
        if (response.data.credentials.prefix) {
          setAutoGenerated(true);
        }
      } else {
        // Mode création
        setHasExistingCredentials(false);
        setExistingCredentialId(null);
        setAutoGenerated(false);
        setCredentialsData({
          username: '',
          password: '',
          prefix: '',
          server_url: '',
          port: '',
          m3u_link: '',
          access_name: '',
          smarters_url: '',
          notes: ''
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des identifiants:', error);
      // En cas d'erreur, on reste en mode création
      setHasExistingCredentials(false);
      setAutoGenerated(false);
      setCredentialsData({
        username: '',
        password: '',
        prefix: '',
        server_url: '',
        port: '',
        m3u_link: '',
        access_name: '',
        smarters_url: '',
        notes: ''
      });
    } finally {
      setCredentialsLoading(false);
      setShowCredentialsModal(true);
    }
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setCredentialsLoading(true);
    
    try {
      if (hasExistingCredentials) {
        // Mise à jour des identifiants existants
        await api.put(`/vendors/sales/${selectedSale.id}/credentials`, credentialsData);
      } else {
        // Création de nouveaux identifiants
        await api.post(`/vendors/sales/${selectedSale.id}/set-credentials`, credentialsData);
      }
      
      setCredentialsSaved(true);
      setTimeout(() => {
        setShowCredentialsModal(false);
        setSelectedSale(null);
        fetchData();
      }, 1500);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des identifiants:', error);
      alert('Erreur lors de la sauvegarde des identifiants');
    } finally {
      setCredentialsLoading(false);
    }
  };

  const getCommissionTier = (salesCount) => {
    // Paliers avec commissions fixes en euros
    if (salesCount >= 40) return { amount: 25, tier: '40+ ventes/mois', color: 'text-[#FFB800]' };
    if (salesCount >= 20) return { amount: 20, tier: '20-39 ventes/mois', color: 'text-[#00FF88]' };
    if (salesCount >= 10) return { amount: 15, tier: '10-19 ventes/mois', color: 'text-[#FF6B35]' };
    return { amount: 10, tier: '0-9 ventes/mois', color: 'text-[#B8B8BA]' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#FF6B35] text-xl">Chargement...</div>
      </div>
    );
  }

  if (!stats?.is_approved) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="pending-approval">
        <div className="max-w-2xl w-full bg-[#1A1A1D] p-10 rounded-lg metallic-border cyber-glow text-center">
          <AlertCircle className="h-20 w-20 text-[#FFB800] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">Candidature en cours d'évaluation</h2>
          <p className="text-[#B8B8BA] text-lg mb-6">
            Votre demande pour devenir vendeur IPTV4KFR est en cours de vérification. Notre équipe vous contactera par email dans les prochaines 24 heures.
          </p>
          <div className="bg-[#222226] p-6 rounded-lg">
            <p className="text-[#B8B8BA] mb-2">En attendant, préparez-vous :</p>
            <ul className="text-[#E8E8E8] text-left space-y-2">
              <li>• Familiarisez-vous avec notre offre IPTV</li>
              <li>• Préparez vos supports de communication</li>
              <li>• Réfléchissez à votre stratégie de vente</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const tier = getCommissionTier(stats?.sales_count || 0);

  return (
    <div className="min-h-screen py-6 sm:py-12 px-3 sm:px-4" data-testid="vendor-dashboard">
      <VendorOnboarding />
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-white mb-1 sm:mb-2">Espace Vendeur</h1>
              <p className="text-sm sm:text-base text-[#B8B8BA]">Bienvenue {user?.email}</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/settings"
                className="flex items-center justify-center gap-2 px-3 sm:px-5 py-2 sm:py-3 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg sm:rounded-xl hover:border-[#00FFFF] transition-all text-white text-sm"
              >
                <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-[#00FFFF]" />
                <span className="hidden sm:inline">Paramètres</span>
              </Link>
              <Link
                to="/assistance"
                className="flex items-center justify-center gap-2 px-3 sm:px-5 py-2 sm:py-3 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg sm:rounded-xl hover:border-[#FF6B35] transition-all text-white text-sm"
                data-testid="vendor-support-link"
              >
                <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
                <span className="hidden sm:inline">Support</span>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid - Mobile: 2 cols, Tablet: 3 cols, Desktop: 6 cols */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="balance-available-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Solde dispo.</span>
              <Euro className="h-4 w-4 sm:h-5 sm:w-5 text-[#00FF88]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-[#00FF88]">{stats?.balance_available?.toFixed(2) || '0.00'}€</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Retirable</div>
          </div>

          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="balance-in-withdrawal-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">En retrait</span>
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-[#FF6B35]">{stats?.balance_in_withdrawal?.toFixed(2) || '0.00'}€</div>
            <div className="text-[#6B6B6E] text-xs mt-1">En cours</div>
          </div>

          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="balance-pending-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">En attente</span>
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-[#FFB800]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-[#FFB800]">{stats?.balance_pending?.toFixed(2) || '0.00'}€</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Non validé</div>
          </div>

          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="total-earned-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Total gagné</span>
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-[#B8B8BA]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-white">{stats?.total_earned?.toFixed(2) || '0.00'}€</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Cumul</div>
          </div>

          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="sales-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Ventes</span>
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#B8B8BA]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-white">{stats?.sales_count || 0}</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Total</div>
          </div>

          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border col-span-2 sm:col-span-1" data-testid="commission-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Commission</span>
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
            </div>
            <div className={`text-xl sm:text-3xl font-bold ${tier.color}`}>{tier.amount}€</div>
            <div className="text-[#6B6B6E] text-xs mt-1 truncate">{tier.tier}</div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8 mb-6 sm:mb-8">
          {/* Vendor Link Section */}
          <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-4 sm:p-8 rounded-lg metallic-border cyber-glow">
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Votre lien vendeur</h2>
            <div className="bg-[#222226] p-3 sm:p-4 rounded-lg mb-3 sm:mb-4">
              <div className="text-[#B8B8BA] text-xs sm:text-sm mb-1 sm:mb-2">Code vendeur</div>
              <div className="text-[#FF6B35] text-lg sm:text-2xl font-mono font-bold">{stats?.vendor_code}</div>
            </div>
            <div className="bg-[#222226] p-3 sm:p-4 rounded-lg">
              <div className="text-[#B8B8BA] text-xs sm:text-sm mb-1 sm:mb-2">Lien d'inscription</div>
              <div className="text-white text-xs sm:text-sm font-mono break-all">{vendorLink}</div>
            </div>
            
            {/* Action Buttons - Mobile: Stack, Desktop: Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-4">
              <button 
                onClick={() => setShowPaymentLinkModal(true)}
                className="btn-primary flex items-center justify-center space-x-2 text-xs sm:text-sm py-2.5"
                data-testid="generate-payment-link-btn"
              >
                <CreditCard className="h-4 w-4" />
                <span>Lien paiement</span>
              </button>
              <Link to="/vendor/statistics" className="btn-secondary text-center flex items-center justify-center text-xs sm:text-sm py-2.5">
                📊 Stats
              </Link>
              <Link to="/vendor/transactions" className="btn-secondary text-center flex items-center justify-center text-xs sm:text-sm py-2.5">
                📜 Transactions
              </Link>
            </div>
            <Link to="/vendor/academy" className="btn-secondary w-full mt-3 text-center flex items-center justify-center text-sm" data-testid="academy-link">
                Accéder à l'Academy
            </Link>
          </div>

          {/* Payout Request Section */}
          <div className="bg-[#1A1A1D] p-4 sm:p-8 rounded-lg metallic-border">
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Demander un paiement</h2>
            
            {payoutSuccess && (
              <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0 mt-0.5" />
                <span className="text-[#00FF88] text-sm">Demande soumise avec succès !</span>
              </div>
            )}

            {payoutError && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-[#FF3B5C] flex-shrink-0 mt-0.5" />
                <span className="text-[#FF3B5C] text-sm">{payoutError}</span>
              </div>
            )}

            <form onSubmit={handlePayoutRequest} className="space-y-4">
              <div>
                <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">Montant (€)</label>
                <input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  min="50"
                  step="0.01"
                  placeholder="Minimum 50€"
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none text-base"
                  data-testid="payout-amount-input"
                />
              </div>
              <button
                type="submit"
                className="w-full btn-primary cyber-glow-hover flex items-center justify-center space-x-2 py-3"
                disabled={!payoutAmount || parseFloat(payoutAmount) < 50}
                data-testid="request-payout-button"
              >
                <Download className="h-5 w-5" />
                <span>Demander le paiement</span>
              </button>
            </form>
            <p className="text-[#6B6B6E] text-xs mt-4">
              * Paiements traités sous 48h via PayPal
            </p>
          </div>
        </div>

        {/* Commission Tiers */}
        <div className="bg-[#1A1A1D] p-4 sm:p-8 rounded-lg metallic-border">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Paliers de commission</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {[
              { range: '0-9', commission: '10€', active: stats?.sales_count < 10 },
              { range: '10-19', commission: '15€', active: stats?.sales_count >= 10 && stats?.sales_count < 20 },
              { range: '20-39', commission: '20€', active: stats?.sales_count >= 20 && stats?.sales_count < 40 },
              { range: '40+', commission: '25€', active: stats?.sales_count >= 40 }
            ].map((tier, index) => (
              <div
                key={index}
                className={`p-3 sm:p-6 rounded-lg text-center ${
                  tier.active
                    ? 'bg-[#FF6B35]/10 border-2 border-[#FF6B35]'
                    : 'bg-[#222226] border border-[#3A3A3D]'
                }`}
              >
                <div className="text-[#B8B8BA] text-xs sm:text-sm mb-1 sm:mb-2">{tier.range} ventes</div>
                <div className={`text-xl sm:text-3xl font-bold ${
                  tier.active ? 'text-[#FF6B35]' : 'text-[#B8B8BA]'
                }`}>
                  {tier.commission}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Clients Section */}
        <div className="mt-6 sm:mt-8 bg-[#1A1A1D] p-4 sm:p-8 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-2xl font-bold text-white">Mes Clients</h2>
            <span className="bg-[#FF6B35]/10 text-[#FF6B35] px-3 sm:px-4 py-1 sm:py-2 rounded-full font-semibold text-sm">
              {clients.length} client{clients.length > 1 ? 's' : ''}
            </span>
          </div>
          {clients.length === 0 ? (
            <div className="text-center py-8 sm:py-12" data-testid="no-clients">
              <UserPlus className="h-12 w-12 sm:h-16 sm:w-16 text-[#3A3A3D] mx-auto mb-4" />
              <p className="text-[#B8B8BA] text-sm sm:text-base">Aucun client inscrit via votre lien</p>
              <p className="text-[#6B6B6E] text-xs sm:text-sm mt-2">Partagez votre lien vendeur pour recruter des clients !</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4" data-testid="clients-list">
              {clients.map((client) => {
                const subscriptionLabel = getSubscriptionLabel(client);
                return (
                <div key={client.id} className="bg-[#222226] rounded-lg overflow-hidden">
                  {/* Client Header - Mobile Optimized */}
                  <div 
                    className="p-3 sm:p-4 cursor-pointer hover:bg-[#2a2a2e] transition-colors"
                    onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center shrink-0">
                          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-white font-semibold text-sm sm:text-base truncate">{client.full_name}</div>
                          <div className="text-[#B8B8BA] text-xs sm:text-sm truncate">{client.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center ml-2">
                        {expandedClient === client.id ? (
                          <ChevronUp className="h-5 w-5 text-[#B8B8BA]" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-[#B8B8BA]" />
                        )}
                      </div>
                    </div>
                    
                    {/* Badges Row - Mobile */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${subscriptionLabel.bg} ${subscriptionLabel.color}`}>
                        {subscriptionLabel.text}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        client.is_active 
                          ? 'bg-[#FF6B35]/10 text-[#FF6B35]' 
                          : 'bg-[#FF3B5C]/10 text-[#FF3B5C]'
                      }`}>
                        {client.is_active ? 'Actif' : 'Désactivé'}
                      </span>
                      {client.referrals_count > 0 && (
                        <span className="bg-[#FFB800]/10 text-[#FFB800] px-2 py-0.5 rounded-full text-xs font-semibold">
                          {client.referrals_count} filleul{client.referrals_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {expandedClient === client.id && (
                    <div className="px-3 sm:px-4 pb-4 border-t border-[#3A3A3D]">
                      {/* Action Buttons */}
                      <div className="flex items-center justify-end gap-2 py-3 border-b border-[#3A3A3D] mb-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedClient(client); setShowEditModal(true); }}
                          className="text-[#FF6B35] hover:text-[#00E5E5] p-2 bg-[#FF6B35]/10 rounded-lg"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => toggleClientStatus(client.id, e)}
                          disabled={actionLoading === client.id}
                          className={`p-2 rounded-lg ${client.is_active ? 'text-[#FFB800] bg-[#FFB800]/10' : 'text-[#00FF88] bg-[#00FF88]/10'}`}
                          title={client.is_active ? 'Désactiver' : 'Activer'}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(client); }}
                          className="text-[#FF3B5C] bg-[#FF3B5C]/10 p-2 rounded-lg"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Client Info */}
                      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                        <div>
                          <span className="text-[#6B6B6E] text-xs">Inscrit le:</span>
                          <div className="text-white text-sm">{formatDate(client.created_at)}</div>
                        </div>
                        <div>
                          <span className="text-[#6B6B6E] text-xs">Appareil:</span>
                          <div className="text-white text-sm">{client.device_type || 'Non renseigné'}</div>
                        </div>
                        <div>
                          <span className="text-[#6B6B6E] text-xs">App IPTV:</span>
                          <div className="text-white text-sm">{client.iptv_app || 'Non renseigné'}</div>
                        </div>
                        <div>
                          <span className="text-[#6B6B6E] text-xs">Adresse MAC:</span>
                          <div className="text-white text-sm font-mono text-xs">{client.mac_address || 'Non renseigné'}</div>
                        </div>
                        {client.device_id && (
                          <div>
                            <span className="text-[#6B6B6E] text-xs">Device ID:</span>
                            <div className="text-white text-sm font-mono text-xs">{client.device_id}</div>
                          </div>
                        )}
                      </div>
                      
                      {/* Referrals */}
                      {client.referrals && client.referrals.length > 0 && (
                        <div>
                          <h4 className="text-[#B8B8BA] font-semibold mb-3 text-sm">Filleuls:</h4>
                          <div className="space-y-2">
                            {client.referrals.map((referral) => (
                              <div key={referral.id} className="bg-[#1A1A1D] p-3 rounded-lg flex items-center justify-between">
                                <div className="min-w-0 flex-1">
                                  <div className="text-white text-sm truncate">{referral.full_name}</div>
                                  <div className="text-[#6B6B6E] text-xs truncate">{referral.email}</div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ml-2 shrink-0 ${
                                  referral.subscription_status === 'active' 
                                    ? 'bg-[#00FF88]/10 text-[#00FF88]' 
                                    : 'bg-[#FF3B5C]/10 text-[#FF3B5C]'
                                }`}>
                                  {referral.subscription_status === 'active' ? 'Actif' : 'Inactif'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sales Section */}
        <div className="mt-6 sm:mt-8 bg-[#1A1A1D] p-4 sm:p-8 rounded-lg metallic-border">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Mes ventes</h2>
          {sales.length === 0 ? (
            <div className="text-center py-8 sm:py-12" data-testid="no-sales">
              <Users className="h-12 w-12 sm:h-16 sm:w-16 text-[#3A3A3D] mx-auto mb-4" />
              <p className="text-[#B8B8BA] text-sm sm:text-base">Aucune vente pour le moment</p>
              <p className="text-[#6B6B6E] text-xs sm:text-sm mt-2">Partagez votre lien pour commencer à vendre !</p>
            </div>
          ) : (
            <>
              {/* Mobile: Card View */}
              <div className="sm:hidden space-y-3" data-testid="sales-cards">
                {sales.map((sale) => (
                  <div key={sale.id} className="bg-[#222226] p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-semibold text-sm truncate">{sale.client_name || 'N/A'}</div>
                        <div className="text-[#B8B8BA] text-xs truncate">{sale.client_email || 'N/A'}</div>
                      </div>
                      {getStatusBadge(sale.subscription_status || sale.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <span className="text-[#6B6B6E] text-xs">Montant:</span>
                        <div className="text-white font-semibold">{sale.amount_paid || 0}€</div>
                      </div>
                      <div>
                        <span className="text-[#6B6B6E] text-xs">Commission:</span>
                        <div className="text-[#FF6B35] font-bold">{sale.commission_earned || 0}€</div>
                      </div>
                      <div>
                        <span className="text-[#6B6B6E] text-xs">Fin abo:</span>
                        <div className="text-white text-sm">
                          {sale.subscription_end 
                            ? new Date(sale.subscription_end).toLocaleDateString('fr-FR')
                            : 'N/A'
                          }
                        </div>
                      </div>
                      <div>
                        <span className="text-[#6B6B6E] text-xs">Date:</span>
                        <div className="text-[#B8B8BA] text-sm">{formatDate(sale.created_at)}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => openCredentialsModal(sale)}
                      className={`w-full px-3 py-2 rounded-lg transition-colors font-semibold text-sm flex items-center justify-center space-x-2 ${
                        sale.has_credentials
                          ? 'bg-[#FFB800] text-[#0A0A0B]'
                          : 'bg-[#FF6B35] text-[#0A0A0B]'
                      }`}
                    >
                      {sale.has_credentials ? (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          <span>Modifier identifiants</span>
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4" />
                          <span>Créer identifiants</span>
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Desktop: Table View */}
              <div className="hidden sm:block overflow-x-auto" data-testid="sales-table">
                <table className="w-full">
                  <thead className="bg-[#222226]">
                    <tr>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Client</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Email</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Montant</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Comm.</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Statut</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Fin abo</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Date</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] font-semibold text-sm">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) => (
                      <tr key={sale.id} className="border-t border-[#3A3A3D] hover:bg-[#222226] transition-colors">
                        <td className="px-4 py-3 text-white text-sm">{sale.client_name || 'N/A'}</td>
                        <td className="px-4 py-3 text-[#B8B8BA] text-sm">{sale.client_email || 'N/A'}</td>
                        <td className="px-4 py-3 text-white font-semibold text-sm">{sale.amount_paid || 0}€</td>
                        <td className="px-4 py-3 text-[#FF6B35] font-bold text-sm">{sale.commission_earned || 0}€</td>
                        <td className="px-4 py-3">{getStatusBadge(sale.subscription_status || sale.status)}</td>
                        <td className="px-4 py-3 text-[#B8B8BA] text-sm">
                          {sale.subscription_end 
                            ? new Date(sale.subscription_end).toLocaleDateString('fr-FR')
                            : 'N/A'
                          }
                        </td>
                        <td className="px-4 py-3 text-[#B8B8BA] text-sm">{formatDate(sale.created_at)}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => openCredentialsModal(sale)}
                            className={`px-3 py-1.5 rounded-lg transition-colors font-semibold text-xs flex items-center space-x-1 ${
                              sale.has_credentials
                                ? 'bg-[#FFB800] text-[#0A0A0B]'
                                : 'bg-[#FF6B35] text-[#0A0A0B]'
                            }`}
                          >
                            {sale.has_credentials ? (
                              <>
                                <CheckCircle className="h-3 w-3" />
                                <span>Modifier</span>
                              </>
                            ) : (
                              <>
                                <UserPlus className="h-3 w-3" />
                                <span>Créer</span>
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirm Modal - Mobile Optimized */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowDeleteConfirm(null)}>
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-6 sm:p-8 rounded-t-2xl sm:rounded-lg metallic-border w-full sm:max-w-md"
            data-testid="delete-client-confirm-modal"
          >
            <div className="text-center">
              <div className="inline-block p-3 sm:p-4 bg-[#FF3B5C]/10 rounded-full mb-4">
                <AlertTriangle className="h-10 w-10 sm:h-12 sm:w-12 text-[#FF3B5C]" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Supprimer ce client ?</h3>
              <p className="text-[#B8B8BA] text-sm sm:text-base mb-6">
                Action <span className="text-[#FF3B5C] font-bold">irréversible</span>. 
                <span className="text-white font-semibold"> {showDeleteConfirm.full_name}</span> sera supprimé.
              </p>
              <div className="flex space-x-3 sm:space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg text-sm sm:text-base"
                >
                  Annuler
                </button>
                <button
                  onClick={() => deleteClient(showDeleteConfirm.id)}
                  disabled={actionLoading === showDeleteConfirm.id}
                  className="flex-1 bg-[#FF3B5C] text-white py-3 rounded-lg disabled:opacity-50 text-sm sm:text-base"
                  data-testid="confirm-delete-client-btn"
                >
                  {actionLoading === showDeleteConfirm.id ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {showEditModal && selectedClient && (
        <ClientEditModal
          client={selectedClient}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClient(null);
            fetchData();
          }}
          onSave={() => fetchData()}
          isAdmin={false}
        />
      )}

      {/* Payment Link Modal - Mobile Optimized */}
      {showPaymentLinkModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={resetPaymentLinkModal}>
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-4 sm:p-8 rounded-t-2xl sm:rounded-lg metallic-border w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
            data-testid="payment-link-modal"
          >
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-2xl font-bold text-white">Créer un lien de paiement</h2>
              <button onClick={resetPaymentLinkModal} className="text-[#B8B8BA] hover:text-white p-2">
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {!linkSuccess ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-[#E8E8E8] mb-2 text-sm">Nom du client *</label>
                  <input
                    type="text"
                    value={paymentLinkData.full_name}
                    onChange={(e) => setPaymentLinkData({...paymentLinkData, full_name: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none text-base"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div>
                  <label className="block text-[#E8E8E8] mb-2 text-sm">Email du client *</label>
                  <input
                    type="email"
                    value={paymentLinkData.email}
                    onChange={(e) => setPaymentLinkData({...paymentLinkData, email: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none text-base"
                    placeholder="client@example.com"
                  />
                </div>
                <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-3 sm:p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-[#B8B8BA] text-xs sm:text-sm">Offre unique</span>
                      <div className="text-white font-bold text-sm sm:text-base">IPTV 12 mois</div>
                    </div>
                    <div className="text-[#FF6B35] font-bold text-lg sm:text-xl">59€</div>
                  </div>
                </div>
                <p className="text-[#6B6B6E] text-xs sm:text-sm">
                  Le client recevra un email avec le lien de paiement.
                </p>
                <button
                  onClick={generatePaymentLink}
                  disabled={linkLoading || !paymentLinkData.email || !paymentLinkData.full_name}
                  className="btn-primary w-full py-3 disabled:opacity-50"
                >
                  {linkLoading ? (
                    <span className="flex items-center justify-center space-x-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Génération...</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center space-x-2">
                      <Send className="h-5 w-5" />
                      <span>Générer et envoyer (59€)</span>
                    </span>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-4 rounded-lg flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-[#00FF88]" />
                  <div>
                    <p className="text-[#00FF88] font-medium">Lien créé et email envoyé !</p>
                    <p className="text-[#B8B8BA] text-sm">Le client a reçu un email avec le lien de paiement.</p>
                  </div>
                </div>
                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">Lien de paiement</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={generatedLink}
                      readOnly
                      className="flex-1 bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm font-mono"
                    />
                    <button onClick={copyPaymentLink} className="btn-secondary py-2 px-4 flex items-center space-x-1">
                      {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span>{linkCopied ? 'Copié!' : 'Copier'}</span>
                    </button>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setLinkSuccess(false);
                      setGeneratedLink('');
                      setPaymentLinkData({ email: '', full_name: '', amount: 59, plan_name: 'Abonnement IPTV 12 mois' });
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Créer un autre lien
                  </button>
                  <button onClick={resetPaymentLinkModal} className="flex-1 btn-primary">
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Modale de gestion des identifiants IPTV */}
      {showCredentialsModal && selectedSale && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowCredentialsModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-2xl w-full mx-4"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">
                {hasExistingCredentials ? 'Modifier les identifiants IPTV' : 'Créer les identifiants IPTV'}
              </h3>
              <button onClick={() => setShowCredentialsModal(false)} className="text-[#B8B8BA] hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="bg-[#222226] p-4 rounded-lg mb-6">
              <p className="text-[#B8B8BA] text-sm">
                Client: <span className="text-white font-semibold">{selectedSale.client_name}</span>
              </p>
              <p className="text-[#B8B8BA] text-sm">
                Email: <span className="text-white">{selectedSale.client_email}</span>
              </p>
              <p className="text-[#B8B8BA] text-sm">
                Abonnement: <span className="text-white">{selectedSale.subscription_id}</span>
              </p>
              {hasExistingCredentials && (
                <div className="mt-2 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded px-3 py-2">
                  <p className="text-[#FF6B35] text-xs font-semibold">✓ Identifiants déjà configurés</p>
                </div>
              )}
            </div>

            {credentialsSaved ? (
              <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-6 rounded-lg text-center">
                <CheckCircle className="h-16 w-16 text-[#00FF88] mx-auto mb-4" />
                <p className="text-[#00FF88] font-semibold text-lg">
                  {hasExistingCredentials ? 'Identifiants mis à jour !' : 'Identifiants sauvegardés !'}
                </p>
                <p className="text-[#B8B8BA] text-sm mt-2">
                  {hasExistingCredentials 
                    ? 'Les identifiants ont été modifiés avec succès.' 
                    : 'Les identifiants ont été enregistrés avec succès.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleCredentialsSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {/* Champ Préfixe - Génération automatique */}
                <div className="bg-[#222226] p-4 rounded-lg border border-[#FF6B35]/30">
                  <label className="block text-[#FF6B35] text-sm mb-2 font-semibold">
                    🔑 Préfixe Client (Génération auto des URLs)
                  </label>
                  <input
                    type="text"
                    value={credentialsData.prefix}
                    onChange={(e) => setCredentialsData({...credentialsData, prefix: e.target.value})}
                    className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#FF6B35] font-mono"
                    placeholder="ex: nbcathrw"
                  />
                  <p className="text-[#6B6B6E] text-xs mt-2">
                    {autoGenerated 
                      ? "✅ URLs générées automatiquement à partir du préfixe" 
                      : "Renseignez le préfixe + identifiants pour générer les URLs automatiquement"
                    }
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-2">Nom d'utilisateur IPTV *</label>
                    <input
                      type="text"
                      value={credentialsData.username}
                      onChange={(e) => setCredentialsData({...credentialsData, username: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#FF6B35] font-mono"
                      placeholder="88430F64F177"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-2">Mot de passe IPTV *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={credentialsData.password}
                      onChange={(e) => setCredentialsData({...credentialsData, password: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 pr-12 text-white focus:outline-none focus:border-[#FF6B35] font-mono"
                      placeholder="EB3F96B5F6A0"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B8B8BA] hover:text-[#FF6B35] transition-colors"
                      title={showPassword ? "Masquer" : "Afficher"}
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                </div>

                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">
                    URL du serveur {!autoGenerated && '*'}
                    {autoGenerated && <span className="ml-2 text-[#00FF88] text-xs">(Auto)</span>}
                  </label>
                  <input
                    type="url"
                    value={credentialsData.server_url}
                    onChange={(e) => !autoGenerated && setCredentialsData({...credentialsData, server_url: e.target.value})}
                    readOnly={autoGenerated}
                    className={`w-full border rounded-lg px-4 py-3 text-white font-mono ${
                      autoGenerated 
                        ? 'bg-[#1A1A1D] border-[#00FF88]/30 cursor-not-allowed text-[#00FF88]' 
                        : 'bg-[#222226] border-[#3A3A3D] focus:outline-none focus:border-[#FF6B35]'
                    }`}
                    placeholder="http://example.com:8080"
                    required={!autoGenerated}
                  />
                </div>

                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">Port (optionnel)</label>
                  <input
                    type="text"
                    value={credentialsData.port}
                    onChange={(e) => setCredentialsData({...credentialsData, port: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#FF6B35]"
                    placeholder="80"
                  />
                </div>

                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">
                    Lien M3U
                    {autoGenerated && <span className="ml-2 text-[#00FF88] text-xs">(Auto)</span>}
                  </label>
                  <input
                    type="url"
                    value={credentialsData.m3u_link}
                    onChange={(e) => !autoGenerated && setCredentialsData({...credentialsData, m3u_link: e.target.value})}
                    readOnly={autoGenerated}
                    className={`w-full border rounded-lg px-4 py-3 text-white font-mono text-sm ${
                      autoGenerated 
                        ? 'bg-[#1A1A1D] border-[#00FF88]/30 cursor-not-allowed text-[#00FF88]' 
                        : 'bg-[#222226] border-[#3A3A3D] focus:outline-none focus:border-[#FF6B35]'
                    }`}
                    placeholder="http://serveur.com/get.php?username=xxx&password=xxx&type=m3u_plus"
                  />
                </div>

                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">Nom de l'accès (optionnel)</label>
                  <input
                    type="text"
                    value={credentialsData.access_name}
                    onChange={(e) => setCredentialsData({...credentialsData, access_name: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#FF6B35]"
                    placeholder="Veuillez nommer cet accès (ex: TV Salon, Box Android...)"
                  />
                </div>

                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">
                    URL IPTV Smarters Samsung / LG
                    {autoGenerated && <span className="ml-2 text-[#00FF88] text-xs">(Auto)</span>}
                  </label>
                  <input
                    type="url"
                    value={credentialsData.smarters_url}
                    onChange={(e) => !autoGenerated && setCredentialsData({...credentialsData, smarters_url: e.target.value})}
                    readOnly={autoGenerated}
                    className={`w-full border rounded-lg px-4 py-3 text-white font-mono text-sm ${
                      autoGenerated 
                        ? 'bg-[#1A1A1D] border-[#00FF88]/30 cursor-not-allowed text-[#00FF88]' 
                        : 'bg-[#222226] border-[#3A3A3D] focus:outline-none focus:border-[#FF6B35]'
                    }`}
                    placeholder="http://example.astrogol.com"
                  />
                  <p className="text-[#6B6B6E] text-xs mt-1">Lien pour l'application IPTV Smarters sur Smart TV Samsung ou LG</p>
                </div>

                <div>
                  <label className="block text-[#B8B8BA] text-sm mb-2">Notes (optionnel)</label>
                  <textarea
                    value={credentialsData.notes}
                    onChange={(e) => setCredentialsData({...credentialsData, notes: e.target.value})}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#FF6B35] h-24 resize-none"
                    placeholder="Ajoutez des instructions ou notes pour le client..."
                  />
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCredentialsModal(false)}
                    className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={credentialsLoading}
                    className="flex-1 bg-[#FF6B35] text-[#0A0A0B] py-3 rounded-lg hover:bg-[#00CCCC] transition-colors font-semibold disabled:opacity-50 flex items-center justify-center"
                  >
                    {credentialsLoading ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                        {hasExistingCredentials ? 'Modification...' : 'Sauvegarde...'}
                      </>
                    ) : (
                      hasExistingCredentials ? 'Mettre à jour' : 'Sauvegarder'
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;